import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import type { DocumentMetadataInterface, ProjectDataInterface } from './types';
import type {
  WorkflowMetadata,
  RiskAssessmentSimulationSettings,
} from 'store/storageunits/types';
import dayjs from 'dayjs';
import { SIMULATION_RESULT } from 'store/simulation/types';

export const initialState: DocumentMetadataInterface = {
  projectData: {
    projectType: '',
    currentDate: '',
    lastSimulation: '',
    customer: '',
    projectLineOne: '',
    projectLineTwo: '',
    projectNo: '',
    flowSheetNo: '',
    drawnBy: '',
    revisedBy: '',
    customLogo: '',
    approvedBy: '',
    userName: '',
  },
  flowsheetMetadata: undefined,
};

const documentMetadataSlice = createSlice({
  name: 'documentMetadata',
  initialState,
  reducers: {
    setProjectData(state, action: PayloadAction<ProjectDataInterface>) {
      state.projectData = action.payload;
    },
    setDrawnBy(state, action: PayloadAction<string>) {
      state.projectData.drawnBy = action.payload;
    },
    setFlowsheetMetadata(
      state,
      action: PayloadAction<WorkflowMetadata | undefined>,
    ) {
      state.flowsheetMetadata = action.payload;
    },
    setRiskAssessmentSimulationSettings(
      state,
      action: PayloadAction<Partial<RiskAssessmentSimulationSettings>>,
    ) {
      state.flowsheetMetadata ??= {};
      state.flowsheetMetadata.riskAssessmentSimulationSettings = {
        simulatePeakCapacity: true,
        simulatePSD: true,
        ...state.flowsheetMetadata.riskAssessmentSimulationSettings,
        ...action.payload,
      };
    },
    setCustomConnectionColor(state, action: PayloadAction<string | undefined>) {
      state.flowsheetMetadata ??= {};
      state.flowsheetMetadata.customConnectionColor = action.payload;
    },
    clearDocumentMetadata() {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(SIMULATION_RESULT, (state) => {
      state.projectData.lastSimulation = dayjs().format('YYYY-MM-DD HH:mm');
    });
  },
});

export const {
  setProjectData,
  setDrawnBy,
  setFlowsheetMetadata,
  setRiskAssessmentSimulationSettings,
  setCustomConnectionColor,
  clearDocumentMetadata,
} = documentMetadataSlice.actions;

export const documentMetadataReducer = documentMetadataSlice.reducer;
